// Web Serial API implementation based on DroneCAN webtools
class WebSerial {
    constructor(port, baudRate) {
        this.port = port;
        this.baudRate = baudRate;
        this.connected = false;
        this.isClosed = true;
        this.reader = null;
        this.writer = null;
        this.readLoopActive = false;
        this.closePromise = null;

        // Event handlers
        this.messageHandlers = [];
        this.openHandlers = [];
        this.errorHandlers = [];
        this.closeHandlers = [];
        this.connectionStatusHandlers = [];
    }

    // Add getter for connection status
    isConnected() {
        return this.connected && this.port && !this.isClosed;
    }

    // Add method to register connection status change handlers
    addConnectionStatusHandler(handler) {
        this.connectionStatusHandlers.push(handler);
    }

    // Update connection status and notify handlers
    updateConnectionStatus(status) {
        if (this.connected !== status) {
            this.connected = status;
            this.connectionStatusHandlers.forEach(handler => handler(status));
        }
    }

    static async requestPort() {
        try {
            if ('serial' in navigator) {
                const port = await navigator.serial.requestPort();
                return port;
            } else {
                throw new Error('Web Serial API not supported in this browser');
            }
        } catch (error) {
            console.error('Error requesting port:', error);
            throw error;
        }
    }

    static async listPorts() {
        try {
            if ('serial' in navigator) {
                const ports = await navigator.serial.getPorts();
                return ports;
            } else {
                throw new Error('Web Serial API not supported in this browser');
            }
        } catch (error) {
            console.error('Error listing ports:', error);
            return [];
        }
    }

    async connect() {
        try {
            if (!this.port) {
                console.error('No port available to connect.');
                return;
            }

            const baudRate = this.baudRate;
            await this.port.open({ baudRate });
            
            this.handleOpen();
            this.isClosed = false;
            
            if (this.port.writable) {
                this.writer = this.port.writable.getWriter();
            }

            if (this.port.readable) {
                this.reader = this.port.readable.getReader();
                const messageHandlers = this.messageHandlers;
                
                this.readLoopActive = true;
                this.updateConnectionStatus(true);
                
                const readLoop = async () => {
                    try {
                        while (this.readLoopActive && this.reader) {
                            const { value, done } = await this.reader.read();
                            if (done) {
                                break;
                            }
                            
                            messageHandlers.forEach(handler => handler(value));
                        }
                    } catch (error) {
                        if (error.name === 'BreakError') {
                            if (this.readLoopActive && !this.isClosed) {
                                try {
                                    if (this.reader) {
                                        this.reader.releaseLock();
                                    }
                                } catch (releaseError) {
                                    console.warn('Error releasing reader after BreakError:', releaseError);
                                }
                                
                                await new Promise(resolve => setTimeout(resolve, 150));
                                
                                if (this.readLoopActive && !this.isClosed && this.port && this.port.readable) {
                                    try {
                                        this.reader = this.port.readable.getReader();
                                        readLoop();
                                    } catch (retryError) {
                                        console.error('Error restarting read loop:', retryError);
                                        this.handleError(retryError);
                                    }
                                }
                            }
                        } else {
                            this.handleError(error);
                            this.updateConnectionStatus(false);
                        }
                    }
                };
                
                readLoop();
            }
        } catch (error) {
            this.handleError(error);
            this.updateConnectionStatus(false);
            throw error;
        }
    }

    handleOpen() {
        this.openHandlers.forEach(handler => handler());
    }

    addOpenHandler(handler) {
        this.openHandlers.push(handler);
    }

    removeOpenHandler(handler) {
        this.openHandlers = this.openHandlers.filter((item) => item !== handler);
    }

    handleMessage(data) {
        if (this.messageHandlers && this.messageHandlers.length > 0) {
            this.messageHandlers.forEach(handler => handler(data));
        } else {
            console.error('Message handlers are not initialized or empty.');
        }
    }

    addMessageHandler(handler) {
        this.messageHandlers.push(handler);
    }

    removeMessageHandler(handler) {
        this.messageHandlers = this.messageHandlers.filter((item) => item !== handler);
    }

    handleError(error) {
        if (error.name === 'BreakError') {
            // console.error('BreakError: Break received');
        } else {
            console.error('Serial port error observed:', error);
            this.errorHandlers.forEach(handler => handler(error));
        }
    }

    addErrorHandler(handler) {
        this.errorHandlers.push(handler);
    }

    removeErrorHandler(handler) {
        this.errorHandlers = this.errorHandlers.filter((item) => item !== handler);
    }

    handleClose() {
        this.closeHandlers.forEach(handler => handler());
    }

    addCloseHandler(handler) {
        this.closeHandlers.push(handler);
    }

    removeCloseHandler(handler) {
        this.closeHandlers = this.closeHandlers.filter((item) => item !== handler);
    }

    async write(data) {
        try {
            if (!this.connected) {
                console.error('Cannot write: not connected to serial port');
                return;
            }
            
            if (!(data instanceof ArrayBuffer || ArrayBuffer.isView(data))) {
                if (typeof data === 'string') {
                    data = new TextEncoder().encode(data);
                } else if (data instanceof Blob) {
                    data = await data.arrayBuffer();
                } else if (Array.isArray(data)) {
                    data = new Uint8Array(data);
                } else {
                    throw new TypeError('The provided value is not of type (ArrayBuffer or ArrayBufferView)');
                }
            }
            await this.writer.write(data);
        } catch (error) {
            this.handleError(error);
            
            if (error.message && error.message.includes('locked') === false) {
                this.updateConnectionStatus(false);
            }
        }
    }

    async close() {
        if (this.closePromise) return this.closePromise;
        this.closePromise = (async () => {
            this.readLoopActive = false;
            this.isClosed = true;
            this.updateConnectionStatus(false);

            if (this.reader) {
                const reader = this.reader;
                this.reader = null;
                try {
                    await reader.cancel();
                } catch (error) {
                    console.warn('Error cancelling reader:', error);
                }
                try {
                    reader.releaseLock();
                } catch (error) {
                    console.warn('Error releasing reader:', error);
                }
            }

            if (this.writer) {
                const writer = this.writer;
                this.writer = null;
                try {
                    writer.releaseLock();
                } catch (error) {
                    console.warn('Error releasing writer:', error);
                }
            }

            if (this.port && (this.port.readable || this.port.writable)) {
                try {
                    await this.port.close();
                } catch (error) {
                    console.warn('Error closing port:', error);
                }
            }

            this.handleClose();
        })();
        try {
            await this.closePromise;
        } finally {
            this.closePromise = null;
        }
    }
}

export default WebSerial;
